/* eslint-disable @typescript-eslint/no-floating-promises */
import React from 'react';
import { Box, Center, css, Global, HStack, Spinner, Stack, Text } from '@chakra-ui/core';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { Header } from '../../../app/topBar/Header';
import { PATH_HIRE_FORM_COPY, PATH_HIRE_FORM_CREATE, PATH_HIRE_FORM_EDIT } from '../../../routes/constants';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';
import ClearAll from './actions/ClearAll';
import SaveTemplate from './actions/Save';
import FinalSignature from './FinalSignature';
import { FormCanvas } from './FormCanvas';
import FormComponentContainer from './formComponentSidebar/FormComponentContainer';
import LanguageSelector from './languageSelect/LanguageSelector';
import { PreviewSidebar } from './previewSidebar/PreviewSidebar';
import TemplateName from './TemplateName';
import { FormMode } from './store/formBuilderModel';
import { useWatchFormDataChange } from './useWatchFormDataChange';

function App(): JSX.Element {
  const { isLoading } = useStoreState((state) => state.formBuilder);
  const { setTemplateName, setFormMode, setClearState, getTemplateById, initializeLanguages } = useStoreActions(
    (state) => state.formBuilder,
  );
  const templateId = useParams<{ templateId?: string }>()?.templateId ?? undefined;
  const history = useHistory();
  const { i18n } = useTranslation();

  const isEdit = !!useRouteMatch(PATH_HIRE_FORM_EDIT);
  const isCopy = !!useRouteMatch(PATH_HIRE_FORM_COPY);
  const isCreateMode = !!useRouteMatch(PATH_HIRE_FORM_CREATE);
  const { currentUserProfile } = useCurrentUserProfile();
  const accountId = currentUserProfile?.account ?? '';
  const location = useLocation();

  React.useEffect(() => {
    let mode: FormMode = 'create';
    if (isEdit) {
      mode = 'edit';
    }
    if (isCopy) {
      mode = 'copy';
    }
    setFormMode(mode);
    // eslint-disable-next-line
  }, [isCopy, isEdit, isCreateMode]);

  React.useEffect(() => {
    if (templateId && !isCreateMode) {
      getTemplateById({ account: accountId, id: templateId, history });
    } else {
      setClearState();
    }
    // eslint-disable-next-line
  }, [templateId]);

  useWatchFormDataChange();

  React.useEffect(() => {
    if (isCreateMode) {
      const queryParams = new URLSearchParams(location.search);
      const defaultLang = (queryParams.get('defaultLanguage') as string) || i18n.language;
      const availableLanguages = queryParams.get('languages') || i18n.language;

      initializeLanguages({
        availableLanguages: availableLanguages.split(','),
        currentLanguage: defaultLang,
        defaultLanguage: defaultLang,
      });

      const templateName = queryParams.get('templateName');

      if (templateName) {
        setTemplateName(templateName);
      }
    }

    // eslint-disable-next-line
  }, [i18n, isCreateMode]);

  return (
    <>
      <Header showMenu />
      <GlobalStyles />

      {isLoading ? (
        <Center flex={1}>
          <Stack align="center">
            <Spinner size="md" color="blue.500" />
            {!isCreateMode && <Text> Building your hire form please wait...</Text>}
          </Stack>
        </Center>
      ) : (
        <Box
          sx={{
            display: 'flex',
            minHeight: '0',
            height: '-webkit-fill-available',
            fontFamily: 'Arial, sans-serif',
            backgroundColor: '#F3F3F3',
          }}
          id="hr-form-builder"
        >
          <FormComponentContainer />
          <Stack flex="0 0 50%">
            <HStack padding={4} align="flex-start">
              <TemplateName />
              <LanguageSelector />
              <ClearAll />
              <SaveTemplate />
            </HStack>
            <FinalSignature />
            <FormCanvas />
          </Stack>
          <Box flex="0 0 25%" overflowY="auto" overflowX="hidden" minHeight="0" height="100%">
            <PreviewSidebar />
          </Box>
        </Box>
      )}
    </>
  );
}

const GlobalStyles = () => (
  <Global
    styles={css`
      html {
        overflow: hidden;
      }
      main {
        min-height: 0;
        height: 100vh;
        display: flex !important;
        flex-direction: column;
        position: fixed;
        width: 100vw;
      }
      main > div:first-child {
        flex-shrink: 0;
      }
    `}
  />
);

export default App;
